import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  updateIncident,
  updateIncidentStatus,
  closeIncident,
  reopenIncident,
  addIncidentNote,
  getIncidentHistory,
  getIncidentNotes,
  getIncidentAttachments,
  escalateIncident,
  getEscalationRules,
  getIncidentTimeline,
  addIncidentAttachment,
  downloadAttachment,
  getIncidentRca,
  createIncidentRca,
  deleteIncidentRca,
  getIncidentActionLinks,
  getIncidentAdminTaxonomy,
  getIncidentWorkflowConfig,
  upsertIncidentActionLink,
} from "./api/incidents.api";
import { listAdminUsers } from "./api/admin.api";
import { getActions } from "./api/actions.api";
import { getControls } from "./api/controls.api";
import { getRisks } from "./riskService";
import Button from "./ui/Button";
import { StatusBadge, Input, Select, Textarea } from "./ui";
import RelatedKpisPanel from "./components/kpi/RelatedKpisPanel";
import DestructiveConfirm from "./components/common/DestructiveConfirm";
import { colors, radius, spacing, typography } from "./ui/tokens";
import { surfaceCardBodyStyle, surfaceCardStyle } from "./ui/pagePatterns";
import useRegisterRecentView from "./features/recentViews/useRegisterRecentView";

const defaultTaxonomyOptions = {
  incident_type: [
    { code: "Operational", label: "Operational" },
    { code: "Security", label: "Security" },
    { code: "Privacy", label: "Privacy" },
    { code: "Compliance", label: "Compliance" },
  ],
  category: [
    { code: "IT", label: "IT" },
    { code: "Security", label: "Security" },
    { code: "ThirdParty", label: "Third Party" },
    { code: "Operations", label: "Operations" },
  ],
  severity: [
    { code: "Low", label: "Low" },
    { code: "Medium", label: "Medium" },
    { code: "High", label: "High" },
    { code: "Critical", label: "Critical" },
  ],
  priority: [
    { code: "Low", label: "Low" },
    { code: "Medium", label: "Medium" },
    { code: "High", label: "High" },
    { code: "Critical", label: "Critical" },
  ],
};
const defaultWorkflowStatuses = ["New", "Investigating", "Mitigated", "Closed"];
const actionLinkTypes = [
  { value: "corrective", label: "Corrective" },
  { value: "preventive", label: "Preventive" },
  { value: "related", label: "Related" },
];
const detailShellStyle = {
  ...surfaceCardStyle,
  padding: spacing.xl + spacing.sm,
};
const sectionStyle = {
  ...surfaceCardStyle,
  ...surfaceCardBodyStyle,
  marginTop: spacing.xl,
  display: "grid",
  gap: spacing.md,
};
const sectionTitleStyle = {
  margin: 0,
  color: colors.text,
  fontSize: typography.size.base,
  fontWeight: typography.weight.bold,
};
const mutedTextStyle = {
  color: colors.textMuted,
  fontSize: typography.size.sm,
};
const metaTextStyle = {
  color: colors.textMuted,
  fontSize: typography.size.xs,
};
const detailHeaderStyle = {
  display: "grid",
  gap: spacing.md,
  paddingBottom: spacing.lg,
  borderBottom: `1px solid ${colors.border}`,
};
const headerMetaRowStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "center",
};
const statePillStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.xs,
  padding: `${spacing.xs}px ${spacing.md}px`,
  borderRadius: radius.pill,
  background: colors.surfaceSubtle,
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: colors.border,
  color: colors.textSecondary,
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
};
const headerDetailsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: spacing.md,
};
const headerDetailLabelStyle = {
  color: colors.textMuted,
  fontSize: typography.size.xs,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};
const headerDetailValueStyle = {
  marginTop: spacing.xs / 2,
  color: colors.text,
  fontSize: typography.size.sm,
  fontWeight: typography.weight.medium,
};
const lifecycleBarStyle = {
  ...surfaceCardStyle,
  ...surfaceCardBodyStyle,
  marginTop: spacing.lg,
  display: "grid",
  gap: spacing.md,
};
const lifecycleActionRowStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "end",
  justifyContent: "space-between",
};
const lifecycleStatusControlsStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "end",
};
const tabHeaderStyle = {
  marginTop: spacing.lg,
  display: "grid",
  gap: spacing.md,
};
const tabListStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "center",
};
const tabButtonStyle = {
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: colors.border,
  borderRadius: radius.pill,
  background: colors.surface,
  color: colors.textSecondary,
  padding: `${spacing.sm}px ${spacing.md}px`,
  fontSize: typography.size.sm,
  fontWeight: typography.weight.medium,
  cursor: "pointer",
  whiteSpace: "nowrap",
};
const tabButtonActiveStyle = {
  background: colors.accentSubtle,
  borderColor: colors.accent,
  color: colors.accentActive,
  boxShadow: `inset 0 0 0 1px ${colors.accent}`,
};
const tabPanelStyle = {
  display: "grid",
  gap: spacing.lg,
};
const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: spacing.md,
};
const summaryFieldStyle = {
  display: "grid",
  gap: spacing.xs / 2,
};
const summaryLabelStyle = {
  color: colors.textMuted,
  fontSize: typography.size.xs,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};
const summaryValueStyle = {
  color: colors.text,
  fontSize: typography.size.sm,
};
const formGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: spacing.lg,
};
const nestedSectionStyle = {
  display: "grid",
  gap: spacing.md,
  paddingTop: spacing.md,
  borderTop: `1px solid ${colors.border}`,
};
const detailTabs = [
  { key: "overview", label: "Overview" },
  { key: "investigation", label: "Investigation" },
  { key: "related", label: "Related" },
  { key: "evidence", label: "Evidence & Notes" },
  { key: "history", label: "History" },
];

function SummaryField({ label, value }) {
  return (
    <div style={summaryFieldStyle}>
      <div style={summaryLabelStyle}>{label}</div>
      <div style={summaryValueStyle}>{value || "Not set"}</div>
    </div>
  );
}

function TabPanel({ tabsId, tabKey, activeTab, children }) {
  if (activeTab !== tabKey) {
    return null;
  }
  return (
    <div
      role="tabpanel"
      id={`${tabsId}-${tabKey}-panel`}
      aria-labelledby={`${tabsId}-${tabKey}-tab`}
      style={tabPanelStyle}
    >
      {children}
    </div>
  );
}

function extractApiError(err, fallback) {
  const source = err?.error || err?.detail || err;
  if (typeof source === "string" && source.trim()) {
    return source;
  }
  if (source?.field_errors && typeof source.field_errors === "object") {
    const message = Object.values(source.field_errors).find((value) => typeof value === "string" && value.trim());
    if (message) {
      return message;
    }
  }
  if (typeof source?.detail === "string" && source.detail.trim()) {
    return source.detail;
  }
  return fallback;
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString() : "-";
}

function normalizeLinkedIds(values) {
  return Array.isArray(values) ? values.map((value) => String(value)).filter(Boolean) : [];
}

function parseLinkedIds(values) {
  return normalizeLinkedIds(values)
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
}

function extractListItems(response) {
  const payload = response?.data ?? response ?? {};
  if (Array.isArray(payload?.items)) {
    return payload.items;
  }
  return Array.isArray(payload) ? payload : [];
}

function buildLinkedOption(item, entityLabel) {
  if (!item?.id && item?.id !== 0) {
    return null;
  }
  const id = String(item.id);
  const baseLabel =
    item.name || item.title || item.control_name || item.action_title || item.code || `${entityLabel} #${id}`;
  const label = baseLabel.includes(`#${id}`) ? baseLabel : `${baseLabel} (#${id})`;
  const keywords = [
    baseLabel,
    item.name,
    item.title,
    item.control_name,
    item.action_title,
    item.category,
    item.status,
    item.owner_name,
    item.code,
    id,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return { value: id, label, keywords };
}

function normalizeLinkedOptions(items, entityLabel) {
  return items
    .map((item) => buildLinkedOption(item, entityLabel))
    .filter(Boolean)
    .sort((left, right) => left.label.localeCompare(right.label));
}

function LinkedEntityMultiSelect({
  label,
  entityLabel,
  selectedIds,
  options,
  onChange,
  disabled,
  loading,
}) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const optionsById = useMemo(() => new Map(options.map((option) => [option.value, option])), [options]);
  const selectedOptions = useMemo(
    () =>
      selectedIds.map((id) => optionsById.get(id) || { value: id, label: `${entityLabel} #${id}`, keywords: id.toLowerCase() }),
    [entityLabel, optionsById, selectedIds]
  );
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const sourceOptions = options.length > 0 ? options : selectedOptions;
    if (!normalizedQuery) {
      return sourceOptions;
    }
    return sourceOptions.filter((option) => option.keywords.includes(normalizedQuery));
  }, [options, query, selectedOptions]);

  const toggleValue = (value) => {
    if (disabled) {
      return;
    }
    onChange(selectedIds.includes(value) ? selectedIds.filter((id) => id !== value) : [...selectedIds, value]);
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef}>
      <div
        style={{
          marginBottom: spacing.xs,
          color: colors.textMuted,
          fontSize: typography.size.xs,
          fontWeight: typography.weight.medium,
        }}
      >
        {label}
      </div>
      <button
        type="button"
        aria-label={`${label} selector`}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
        disabled={disabled}
        style={{
          width: "100%",
          minHeight: 42,
          padding: `${spacing.md}px ${spacing.lg}px`,
          borderRadius: radius.sm,
          border: `1px solid ${colors.border}`,
          background: disabled ? colors.disabledBg : colors.surface,
          color: disabled ? colors.disabledText : colors.text,
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        {selectedOptions.length > 0
          ? `${selectedOptions.length} selected`
          : `Select ${label.toLowerCase()}`}
      </button>
      {selectedOptions.length > 0 && (
        <div style={{ marginTop: spacing.sm, display: "flex", flexWrap: "wrap", gap: spacing.sm }}>
          {selectedOptions.map((option) => (
            <span
              key={option.value}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: spacing.xs,
                padding: `${spacing.xs}px ${spacing.md}px`,
                borderRadius: radius.pill,
                background: colors.accentSubtle,
                color: colors.accentActive,
                fontSize: typography.size.xs,
              }}
            >
              <span>{option.label}</span>
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${option.label}`}
                  onClick={() => toggleValue(option.value)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: colors.accentActive,
                    cursor: "pointer",
                    padding: 0,
                    fontSize: typography.size.xs,
                  }}
                >
                  x
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {isOpen && (
        <div
          style={{
            marginTop: spacing.sm,
            padding: spacing.md,
            borderRadius: radius.md,
            border: `1px solid ${colors.border}`,
            background: colors.surface,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Input
            value={query}
            disabled={disabled}
            ariaLabel={`Search ${label.toLowerCase()}`}
            placeholder={`Search ${entityLabel.toLowerCase()} by keyword`}
            onChange={(event) => setQuery(event.target.value)}
            style={{ width: "100%" }}
          />
          <div style={{ marginTop: spacing.sm, color: colors.textMuted, fontSize: typography.size.xs }}>
            {loading ? `Loading ${label.toLowerCase()}...` : `Showing ${filteredOptions.length} ${label.toLowerCase()}`}
          </div>
          <div
            style={{
              marginTop: spacing.sm,
              maxHeight: 220,
              overflowY: "auto",
              display: "grid",
              gap: spacing.xs,
            }}
          >
            {filteredOptions.length === 0 ? (
              <div style={{ color: colors.textMuted, fontSize: typography.size.sm }}>No matches found.</div>
            ) : (
              filteredOptions.map((option) => (
                <label
                  key={option.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacing.sm,
                    padding: `${spacing.sm}px ${spacing.md}px`,
                    borderRadius: radius.sm,
                    background: selectedIds.includes(option.value) ? colors.surfaceSubtle : colors.surface,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(option.value)}
                    disabled={disabled}
                    onChange={() => toggleValue(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function IncidentDetails({
  incident,
  onUpdated,
  canEdit = false,
  canManage = false,
  shellStyle,
}) {
  const tabsId = useId();
  const tabRefs = useRef({});
  const [draft, setDraft] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [statusDraft, setStatusDraft] = useState("New");
  const [noteText, setNoteText] = useState("");
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [rules, setRules] = useState(null);
  const [rcaEntries, setRcaEntries] = useState([]);
  const [actionLinks, setActionLinks] = useState([]);
  const [taxonomyOptions, setTaxonomyOptions] = useState(defaultTaxonomyOptions);
  const [workflowStatuses, setWorkflowStatuses] = useState(defaultWorkflowStatuses);
  const [rcaDraft, setRcaDraft] = useState({ cause_type: "", summary: "", contributing_factor: "" });
  const [actionLinkDraft, setActionLinkDraft] = useState({
    action_id: "",
    link_type: "corrective",
    required_for_closure: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmEscalate, setConfirmEscalate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [resolutionSummary, setResolutionSummary] = useState("");
  const [reopenReason, setReopenReason] = useState("");
  const [showClosurePanel, setShowClosurePanel] = useState(false);
  const [linkedRiskOptions, setLinkedRiskOptions] = useState([]);
  const [linkedControlOptions, setLinkedControlOptions] = useState([]);
  const [linkedActionOptions, setLinkedActionOptions] = useState([]);
  const [linkedOptionsLoading, setLinkedOptionsLoading] = useState({
    risks: false,
    controls: false,
    actions: false,
  });
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownersError, setOwnersError] = useState("");
  useRegisterRecentView({
    itemType: "incident",
    itemId: incident?.id,
    title: incident?.title,
    secondaryText: incident?.status || incident?.severity || null,
    enabled: Boolean(incident?.id && incident?.title),
  });

  useEffect(() => {
    let active = true;
    const loadConfig = async () => {
      try {
        const [typeRes, categoryRes, severityRes, priorityRes, workflowRes] = await Promise.all([
          getIncidentAdminTaxonomy("incident_type"),
          getIncidentAdminTaxonomy("category"),
          getIncidentAdminTaxonomy("severity"),
          getIncidentAdminTaxonomy("priority"),
          getIncidentWorkflowConfig(),
        ]);
        if (!active) return;
        setTaxonomyOptions({
          incident_type: (typeRes.data || typeRes).filter((item) => item.active),
          category: (categoryRes.data || categoryRes).filter((item) => item.active),
          severity: (severityRes.data || severityRes).filter((item) => item.active),
          priority: (priorityRes.data || priorityRes).filter((item) => item.active),
        });
        const statusCodes = ((workflowRes.data || workflowRes).statuses || [])
          .filter((item) => item.active)
          .map((item) => item.code);
        setWorkflowStatuses(statusCodes.length > 0 ? statusCodes : defaultWorkflowStatuses);
      } catch (_err) {
        if (!active) return;
        setTaxonomyOptions(defaultTaxonomyOptions);
        setWorkflowStatuses(defaultWorkflowStatuses);
      }
    };
    loadConfig();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const loadLinkedOptions = async () => {
      setLinkedOptionsLoading({
        risks: true,
        controls: true,
        actions: true,
      });

      const [riskResult, controlResult, actionResult] = await Promise.allSettled([
        getRisks({ limit: 200 }),
        getControls({ limit: 200 }),
        getActions({ limit: 200 }),
      ]);

      if (!active) {
        return;
      }

      setLinkedRiskOptions(
        riskResult.status === "fulfilled" ? normalizeLinkedOptions(extractListItems(riskResult.value), "Risk") : []
      );
      setLinkedControlOptions(
        controlResult.status === "fulfilled"
          ? normalizeLinkedOptions(extractListItems(controlResult.value), "Control")
          : []
      );
      setLinkedActionOptions(
        actionResult.status === "fulfilled" ? normalizeLinkedOptions(extractListItems(actionResult.value), "Action") : []
      );
      setLinkedOptionsLoading({
        risks: false,
        controls: false,
        actions: false,
      });
    };

    loadLinkedOptions();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadOwners = async () => {
      setOwnersLoading(true);
      setOwnersError("");
      try {
        const response = await listAdminUsers();
        if (!active) {
          return;
        }
        const users = Array.isArray(response?.data) ? response.data : [];
        setOwnerOptions(
          users
            .filter((user) => user?.username)
            .map((user) => ({
              value: String(user.username),
              label: user.email ? `${user.username} (${user.email})` : String(user.username),
            }))
            .sort((left, right) => left.label.localeCompare(right.label))
        );
      } catch (err) {
        if (!active) {
          return;
        }
        setOwnerOptions([]);
        setOwnersError(err?.response?.data?.detail || err?.message || "Unable to load owners.");
      } finally {
        if (active) {
          setOwnersLoading(false);
        }
      }
    };

    loadOwners();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (incident) {
      setActiveTab("overview");
      setDraft({
        title: incident.title || "",
        description: incident.description || "",
        incident_type: incident.incident_type || "",
        category: incident.category || "",
        severity: incident.severity || "Low",
        priority: incident.priority || "",
        owner_id: incident.owner_id || "",
        impact_summary: incident.impact_summary || "",
        resolution_summary: incident.resolution_summary || "",
        root_cause_summary: incident.root_cause_summary || "",
        occurred_at: incident.occurred_at ? incident.occurred_at.slice(0, 16) : "",
        risk_ids: normalizeLinkedIds(incident.risk_ids),
        control_ids: normalizeLinkedIds(incident.control_ids),
        action_ids: normalizeLinkedIds(incident.action_ids),
      });
      setStatusDraft(incident.status || "New");
      setResolutionSummary(incident.resolution_summary || "");
      setReopenReason("");
      setShowClosurePanel(incident.status === "Closed");
      setError("");
      setRcaDraft({ cause_type: "", summary: "", contributing_factor: "" });
      setActionLinkDraft({ action_id: "", link_type: "corrective", required_for_closure: false });
      loadHistory(incident.id);
      loadNotes(incident.id);
      loadAttachments(incident.id);
      loadTimeline(incident.id);
      loadRules();
      loadRca(incident.id);
      loadActionLinks(incident.id);
    } else {
      setDraft(null);
      setActiveTab("overview");
      setShowClosurePanel(false);
      setHistory([]);
      setNoteText("");
      setNotes([]);
      setAttachments([]);
      setTimeline(null);
      setRules(null);
      setRcaEntries([]);
      setActionLinks([]);
    }
  }, [incident]);

  const slaBadge = useMemo(() => {
    if (!incident) return null;
    if (incident.sla_breached) return { label: "SLA Breached", color: colors.danger };
    if (incident.sla_due_at)
      return { label: `SLA Due ${new Date(incident.sla_due_at).toLocaleString()}`, color: colors.accent };
    return null;
  }, [incident]);

  const ownerSelectOptions = useMemo(() => {
    const currentOwnerId = String(draft?.owner_id || "").trim();
    const options = [...ownerOptions];
    if (currentOwnerId && !options.some((option) => option.value === currentOwnerId)) {
      options.unshift({ value: currentOwnerId, label: currentOwnerId });
    }
    return options;
  }, [draft?.owner_id, ownerOptions]);

  const isEscalated = useMemo(
    () => Boolean(incident?.escalated_at || Number(incident?.escalation_level || 0) > 0),
    [incident?.escalated_at, incident?.escalation_level]
  );

  const isBreached = useMemo(
    () => Boolean(incident?.sla_breached || incident?.breach_at),
    [incident?.sla_breached, incident?.breach_at]
  );

  const loadHistory = async (incidentId) => {
    if (!incidentId) return;
    try {
      const res = await getIncidentHistory(incidentId);
      setHistory(res.data || res);
    } catch (_err) {
      setHistory([]);
    }
  };

  const loadNotes = async (incidentId) => {
    if (!incidentId) return;
    try {
      const res = await getIncidentNotes(incidentId);
      setNotes(res.data || res);
    } catch (_err) {
      setNotes([]);
    }
  };

  const loadAttachments = async (incidentId) => {
    if (!incidentId) return;
    try {
      const res = await getIncidentAttachments(incidentId);
      setAttachments(res.data || res);
    } catch (_err) {
      setAttachments([]);
    }
  };

  const loadTimeline = async (incidentId) => {
    if (!incidentId) return;
    try {
      const res = await getIncidentTimeline(incidentId);
      setTimeline(res.data || res);
    } catch (_err) {
      setTimeline(null);
    }
  };

  const loadRules = async () => {
    try {
      const res = await getEscalationRules();
      setRules(res?.data?.rules || res?.data || null);
    } catch (_err) {
      setRules(null);
    }
  };

  const loadRca = async (incidentId) => {
    if (!incidentId) return;
    try {
      const res = await getIncidentRca(incidentId);
      setRcaEntries(res.data || res);
    } catch (_err) {
      setRcaEntries([]);
    }
  };

  const loadActionLinks = async (incidentId) => {
    if (!incidentId) return;
    try {
      const res = await getIncidentActionLinks(incidentId);
      setActionLinks(res.data || res);
    } catch (_err) {
      setActionLinks([]);
    }
  };

  if (!incident) {
    return <div style={{ padding: spacing.md * 2, fontStyle: "italic" }}>Select an incident to view details.</div>;
  }

  const statusTransitionOptions =
    incident.status === "Closed" ? ["Closed"] : workflowStatuses.filter((status) => status !== "Closed");

  const handleSave = async () => {
    if (!draft || !canEdit) return;
    setLoading(true);
    setError("");
    try {
      const payload = {
        title: draft.title,
        description: draft.description || null,
        incident_type: draft.incident_type || null,
        category: draft.category || null,
        severity: draft.severity,
        priority: draft.priority || null,
        owner_id: draft.owner_id || null,
        impact_summary: draft.impact_summary || null,
        resolution_summary: draft.resolution_summary || null,
        root_cause_summary: draft.root_cause_summary || null,
        occurred_at: draft.occurred_at || null,
        risk_ids: parseLinkedIds(draft.risk_ids),
        control_ids: parseLinkedIds(draft.control_ids),
        action_ids: parseLinkedIds(draft.action_ids),
      };
      const res = await updateIncident(incident.id, payload);
      onUpdated?.(res.data || res);
    } catch (err) {
      setError(extractApiError(err, "Failed to update incident"));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusSave = async () => {
    if (!canEdit || incident.status === "Closed") return;
    setLoading(true);
    setError("");
    try {
      const res = await updateIncidentStatus(incident.id, { status: statusDraft });
      onUpdated?.(res.data || res);
      await loadHistory(incident.id);
    } catch (err) {
      setError(extractApiError(err, "Failed to update status"));
    } finally {
      setLoading(false);
    }
  };

  const requestStatusSave = () => {
    if (!canEdit || incident.status === "Closed") return;
    if (statusDraft === "Closed") {
      requestCloseIncident();
      return;
    }
    handleStatusSave();
  };

  const handleCloseIncident = async () => {
    if (!canManage) return;
    const summary = resolutionSummary.trim();
    if (!summary) {
      setError("Resolution summary is required to close an incident");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await closeIncident(incident.id, { resolution_summary: summary });
      onUpdated?.(res.data || res);
      await loadHistory(incident.id);
      await loadTimeline(incident.id);
      await loadActionLinks(incident.id);
    } catch (err) {
      setError(extractApiError(err, "Failed to close incident"));
    } finally {
      setLoading(false);
    }
  };

  const requestCloseIncident = () => {
    if (!canManage) return;
    if (!resolutionSummary.trim()) {
      setError("Resolution summary is required to close an incident");
      return;
    }
    setError("");
    setConfirmClose(true);
  };

  const handleReopenIncident = async () => {
    if (!canManage) return;
    const reason = reopenReason.trim();
    if (!reason) {
      setError("Reopen reason is required");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await reopenIncident(incident.id, { reopen_reason: reason });
      onUpdated?.(res.data || res);
      setReopenReason("");
      await loadHistory(incident.id);
      await loadTimeline(incident.id);
    } catch (err) {
      setError(extractApiError(err, "Failed to reopen incident"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!canEdit || !noteText.trim()) return;
    setLoading(true);
    setError("");
    try {
      await addIncidentNote(incident.id, { body: noteText.trim() });
      await loadHistory(incident.id);
      await loadNotes(incident.id);
      setNoteText("");
    } catch (err) {
      setError(extractApiError(err, "Failed to add note"));
    } finally {
      setLoading(false);
    }
  };

  const handleEscalate = async () => {
    if (!canEdit) return;
    setLoading(true);
    setError("");
    try {
      const res = await escalateIncident(incident.id);
      onUpdated?.(res.data || res);
      await loadHistory(incident.id);
      await loadNotes(incident.id);
      await loadAttachments(incident.id);
      await loadTimeline(incident.id);
    } catch (err) {
      setError(extractApiError(err, "Failed to escalate incident"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddRca = async () => {
    if (!canEdit) return;
    if (!rcaDraft.summary.trim()) {
      setError("RCA summary is required");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await createIncidentRca(incident.id, {
        cause_type: rcaDraft.cause_type || null,
        summary: rcaDraft.summary.trim(),
        contributing_factor: rcaDraft.contributing_factor || null,
      });
      setRcaDraft({ cause_type: "", summary: "", contributing_factor: "" });
      await loadRca(incident.id);
      await loadHistory(incident.id);
    } catch (err) {
      setError(extractApiError(err, "Failed to add RCA entry"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRca = async (rcaId) => {
    if (!canEdit) return;
    setLoading(true);
    setError("");
    try {
      await deleteIncidentRca(incident.id, rcaId);
      await loadRca(incident.id);
      await loadHistory(incident.id);
    } catch (err) {
      setError(extractApiError(err, "Failed to delete RCA entry"));
    } finally {
      setLoading(false);
    }
  };

  const handleUpsertActionLink = async () => {
    if (!canEdit) return;
    const actionId = Number(actionLinkDraft.action_id);
    if (Number.isNaN(actionId) || actionId <= 0) {
      setError("Action ID is required");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await upsertIncidentActionLink(incident.id, actionId, {
        link_type: actionLinkDraft.link_type,
        required_for_closure: Boolean(actionLinkDraft.required_for_closure),
      });
      setActionLinkDraft({ action_id: "", link_type: "corrective", required_for_closure: false });
      await loadActionLinks(incident.id);
      await loadHistory(incident.id);
    } catch (err) {
      setError(extractApiError(err, "Failed to update remediation link"));
    } finally {
      setLoading(false);
    }
  };

  const requestEscalate = () => {
    if (!canEdit) return;
    setConfirmEscalate(true);
  };

  const openClosurePanel = () => {
    if (!canManage) return;
    setActiveTab("overview");
    setShowClosurePanel(true);
  };

  const handleTabKeyDown = (event, index) => {
    const lastIndex = detailTabs.length - 1;
    let nextIndex = null;

    if (event.key === "ArrowRight") {
      nextIndex = index === lastIndex ? 0 : index + 1;
    } else if (event.key === "ArrowLeft") {
      nextIndex = index === 0 ? lastIndex : index - 1;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = lastIndex;
    }

    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = detailTabs[nextIndex];
    setActiveTab(nextTab.key);
    tabRefs.current[nextTab.key]?.focus();
  };

  return (
    <div
      style={{
        ...detailShellStyle,
        ...shellStyle,
      }}
    >
      <div style={detailHeaderStyle}>
        <div style={{ display: "grid", gap: spacing.sm }}>
          <div style={headerMetaRowStyle}>
            <span style={{ ...headerDetailLabelStyle, margin: 0 }}>Incident</span>
          </div>
          <h3 style={{ margin: 0, color: colors.text, fontSize: typography.size.xl, fontWeight: typography.weight.bold }}>
            {draft?.title || incident.title}
          </h3>
          <div style={headerMetaRowStyle}>
            <StatusBadge variant="severity" value={draft?.severity || incident.severity} />
            <StatusBadge value={statusDraft || incident.status} fallbackLabel={statusDraft || incident.status || "-"} />
            {draft?.priority || incident.priority ? <span style={statePillStyle}>Priority {draft?.priority || incident.priority}</span> : null}
            {slaBadge ? (
              <span
                style={{
                  ...statePillStyle,
                  background: colors.accentSubtle,
                  borderColor: colors.accent,
                  color: colors.accentActive,
                }}
              >
                {slaBadge.label}
              </span>
            ) : null}
            {isBreached ? (
              <span
                style={{
                  ...statePillStyle,
                  background: "rgba(220, 38, 38, 0.08)",
                  borderColor: colors.danger,
                  color: colors.danger,
                }}
              >
                Breached
              </span>
            ) : null}
            {isEscalated ? <span style={statePillStyle}>Escalated</span> : null}
          </div>
        </div>

        <div style={headerDetailsGridStyle}>
          <div>
            <div style={headerDetailLabelStyle}>Owner</div>
            <div style={headerDetailValueStyle}>{draft?.owner_id || incident.owner_id || "Unassigned"}</div>
          </div>
          <div>
            <div style={headerDetailLabelStyle}>Reported</div>
            <div style={headerDetailValueStyle}>{formatDateTime(incident.reported_at)}</div>
          </div>
          <div>
            <div style={headerDetailLabelStyle}>Type</div>
            <div style={headerDetailValueStyle}>{draft?.incident_type || incident.incident_type || "Not set"}</div>
          </div>
          <div>
            <div style={headerDetailLabelStyle}>Category</div>
            <div style={headerDetailValueStyle}>{draft?.category || incident.category || "Not set"}</div>
          </div>
        </div>
      </div>

      {error && <div style={{ color: colors.danger, marginTop: spacing.md }}>{error}</div>}

      <div style={lifecycleBarStyle}>
        <div style={lifecycleActionRowStyle}>
          <div style={lifecycleStatusControlsStyle}>
            <Select
              label="Status"
              value={statusDraft}
              disabled={!canEdit || incident.status === "Closed"}
              onChange={(e) => setStatusDraft(e.target.value)}
              style={{ minWidth: 180 }}
            >
              {statusTransitionOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
            <Button
              onClick={requestStatusSave}
              disabled={!canEdit || loading || incident.status === "Closed"}
              variant="secondary"
              style={{ marginTop: 18 }}
            >
              Update Status
            </Button>
          </div>

          <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
            <Button onClick={handleSave} disabled={!canEdit || loading} variant="primary">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
            <Button onClick={requestEscalate} disabled={!canEdit || loading} variant="destructive">
              Escalate
            </Button>
            {canManage && incident.status !== "Closed" && (
              <Button onClick={openClosurePanel} disabled={loading} variant="secondary">
                Close Incident
              </Button>
            )}
          </div>
        </div>
        <div style={mutedTextStyle}>
          Ordinary status updates remain separate from the dedicated close and reopen workflow.
        </div>
      </div>

      <div style={tabHeaderStyle}>
        <div role="tablist" aria-label="Incident detail sections" style={tabListStyle}>
          {detailTabs.map((tab, index) => {
            const selected = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                ref={(element) => {
                  tabRefs.current[tab.key] = element;
                }}
                id={`${tabsId}-${tab.key}-tab`}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls={`${tabsId}-${tab.key}-panel`}
                tabIndex={selected ? 0 : -1}
                style={{
                  ...tabButtonStyle,
                  ...(selected ? tabButtonActiveStyle : null),
                }}
                onClick={() => setActiveTab(tab.key)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <TabPanel tabsId={tabsId} tabKey="overview" activeTab={activeTab}>
        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Overview</h4>
          <div style={summaryGridStyle}>
            <SummaryField label="Reported" value={formatDateTime(incident.reported_at)} />
            <SummaryField label="Occurred" value={draft?.occurred_at ? formatDateTime(draft.occurred_at) : "Not set"} />
            <SummaryField label="Priority" value={draft?.priority || incident.priority || "Not set"} />
            <SummaryField label="Created" value={formatDateTime(incident.created_at)} />
            <SummaryField label="Last updated" value={formatDateTime(incident.updated_at)} />
            <SummaryField
              label="Escalation level"
              value={incident.escalation_level != null ? String(incident.escalation_level) : "Not escalated"}
            />
          </div>
        </div>

        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Core Incident Information</h4>
          <div style={formGridStyle}>
            <label>
              Title
              <Input
                value={draft?.title || ""}
                disabled={!canEdit}
                onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
              />
            </label>
            <label>
              Owner
              <Select
                value={draft?.owner_id || ""}
                disabled={!canEdit || ownersLoading}
                onChange={(e) => setDraft((prev) => ({ ...prev, owner_id: e.target.value }))}
              >
                <option value="">{ownersLoading ? "Loading owners..." : "Select owner"}</option>
                {ownerSelectOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              {ownersError && ownerSelectOptions.length === 0 && (
                <div style={{ color: colors.danger, fontSize: typography.size.xs, marginTop: spacing.xs }}>{ownersError}</div>
              )}
            </label>
            <label>
              Type
              <Select
                value={draft?.incident_type || ""}
                disabled={!canEdit}
                onChange={(e) => setDraft((prev) => ({ ...prev, incident_type: e.target.value }))}
              >
                <option value="">Select type</option>
                {taxonomyOptions.incident_type.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label || option.code}
                  </option>
                ))}
              </Select>
            </label>
            <label>
              Category
              <Select
                value={draft?.category || ""}
                disabled={!canEdit}
                onChange={(e) => setDraft((prev) => ({ ...prev, category: e.target.value }))}
              >
                <option value="">Select category</option>
                {taxonomyOptions.category.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label || option.code}
                  </option>
                ))}
              </Select>
            </label>
            <label>
              Severity
              <Select
                value={draft?.severity || "Low"}
                disabled={!canEdit}
                onChange={(e) => setDraft((prev) => ({ ...prev, severity: e.target.value }))}
              >
                {taxonomyOptions.severity.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label || option.code}
                  </option>
                ))}
              </Select>
            </label>
            <label>
              Priority
              <Select
                value={draft?.priority || ""}
                disabled={!canEdit}
                onChange={(e) => setDraft((prev) => ({ ...prev, priority: e.target.value }))}
              >
                <option value="">No priority</option>
                {taxonomyOptions.priority.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label || option.code}
                  </option>
                ))}
              </Select>
            </label>
            <label>
              Occurred At
              <Input
                type="datetime-local"
                value={draft?.occurred_at || ""}
                disabled={!canEdit}
                onChange={(e) => setDraft((prev) => ({ ...prev, occurred_at: e.target.value }))}
              />
            </label>
          </div>

          <div style={nestedSectionStyle}>
            <div style={{ display: "grid", gap: spacing.md }}>
              <label>
                Description
                <Textarea
                  rows={4}
                  value={draft?.description || ""}
                  disabled={!canEdit}
                  onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
                />
              </label>
              <label>
                Impact Summary
                <Textarea
                  rows={3}
                  value={draft?.impact_summary || ""}
                  disabled={!canEdit}
                  onChange={(e) => setDraft((prev) => ({ ...prev, impact_summary: e.target.value }))}
                />
              </label>
            </div>
          </div>
        </div>

        {(showClosurePanel || incident.status === "Closed") && (
          <div style={sectionStyle}>
            <h4 style={sectionTitleStyle}>Closure</h4>
            <div style={summaryGridStyle}>
              <SummaryField
                label="Closed"
                value={incident.closed_at ? `${new Date(incident.closed_at).toLocaleString()} by ${incident.closed_by || "-"}` : "-"}
              />
              <SummaryField
                label="Reopened"
                value={incident.reopened_at ? `${new Date(incident.reopened_at).toLocaleString()} by ${incident.reopened_by || "-"}` : "-"}
              />
            </div>
            <label>
              Resolution required for close
              <Textarea
                rows={3}
                value={resolutionSummary}
                disabled={!canManage || incident.status === "Closed"}
                onChange={(e) => {
                  setResolutionSummary(e.target.value);
                  setDraft((prev) => ({ ...prev, resolution_summary: e.target.value }));
                }}
              />
            </label>
            {canManage && incident.status !== "Closed" && (
              <div style={{ display: "flex", gap: spacing.md, flexWrap: "wrap" }}>
                <Button
                  onClick={requestCloseIncident}
                  disabled={loading || incident.status === "Closed"}
                  variant="destructive"
                >
                  Confirm Close
                </Button>
                <Button onClick={() => setShowClosurePanel(false)} disabled={loading} variant="secondary">
                  Hide Closure
                </Button>
              </div>
            )}
            {incident.status === "Closed" && canManage && (
              <>
                <label>
                  Reopen reason
                  <Input
                    value={reopenReason}
                    disabled={loading}
                    onChange={(e) => setReopenReason(e.target.value)}
                  />
                </label>
                <Button
                  onClick={handleReopenIncident}
                  disabled={loading || !reopenReason.trim()}
                  variant="secondary"
                >
                  Reopen Incident
                </Button>
              </>
            )}
            {incident.reopen_reason && <div style={mutedTextStyle}>Last reopen reason: {incident.reopen_reason}</div>}
          </div>
        )}
      </TabPanel>

      <TabPanel tabsId={tabsId} tabKey="investigation" activeTab={activeTab}>
        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Investigation Summary</h4>
          <label>
            Root Cause Summary
            <Textarea
              rows={3}
              value={draft?.root_cause_summary || ""}
              disabled={!canEdit}
              onChange={(e) => setDraft((prev) => ({ ...prev, root_cause_summary: e.target.value }))}
            />
          </label>
        </div>

        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Root Cause Analysis</h4>
          {rcaEntries.length === 0 && <div style={mutedTextStyle}>No RCA entries yet.</div>}
          <ul style={{ paddingLeft: spacing.md * 2 }}>
            {rcaEntries.map((entry) => (
              <li key={entry.id} style={{ marginBottom: spacing.md }}>
                <div>
                  <strong>{entry.cause_type || "Unclassified"}</strong>
                </div>
                <div>{entry.summary}</div>
                {entry.contributing_factor && (
                  <div style={mutedTextStyle}>Contributing factor: {entry.contributing_factor}</div>
                )}
                <div style={metaTextStyle}>{formatDateTime(entry.created_at)}</div>
                {canEdit && (
                  <Button
                    onClick={() => handleDeleteRca(entry.id)}
                    disabled={loading}
                    variant="secondary"
                    style={{ marginTop: spacing.xs }}
                  >
                    Delete RCA
                  </Button>
                )}
              </li>
            ))}
          </ul>
          {canEdit && (
            <div style={{ display: "grid", gap: spacing.sm }}>
              <Input
                value={rcaDraft.cause_type}
                onChange={(e) => setRcaDraft((prev) => ({ ...prev, cause_type: e.target.value }))}
                placeholder="Cause type"
              />
              <Textarea
                rows={2}
                value={rcaDraft.summary}
                onChange={(e) => setRcaDraft((prev) => ({ ...prev, summary: e.target.value }))}
                placeholder="RCA finding"
              />
              <Input
                value={rcaDraft.contributing_factor}
                onChange={(e) => setRcaDraft((prev) => ({ ...prev, contributing_factor: e.target.value }))}
                placeholder="Contributing factor"
              />
              <div>
                <Button onClick={handleAddRca} disabled={loading} variant="secondary">
                  Add RCA Entry
                </Button>
              </div>
            </div>
          )}
        </div>

        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Remediation Links</h4>
          {actionLinks.length === 0 && <div style={mutedTextStyle}>No remediation links yet.</div>}
          <ul style={{ paddingLeft: spacing.md * 2 }}>
            {actionLinks.map((link) => (
              <li key={link.action_id} style={{ marginBottom: spacing.sm }}>
                <div>
                  <strong>{link.action_title || link.action_name || `Action ${link.action_id}`}</strong>
                </div>
                <div>
                  Type: {link.link_type} | Required for closure: {link.required_for_closure ? "Yes" : "No"}
                </div>
                <div style={mutedTextStyle}>Status: {link.action_status_current || link.action_status_snapshot || "-"}</div>
              </li>
            ))}
          </ul>
          {canEdit && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: spacing.md, alignItems: "end" }}>
              <label>
                Action ID
                <Input
                  value={actionLinkDraft.action_id}
                  onChange={(e) => setActionLinkDraft((prev) => ({ ...prev, action_id: e.target.value }))}
                />
              </label>
              <label>
                Link Type
                <Select
                  value={actionLinkDraft.link_type}
                  onChange={(e) => setActionLinkDraft((prev) => ({ ...prev, link_type: e.target.value }))}
                >
                  {actionLinkTypes.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </label>
              <label style={{ display: "flex", gap: spacing.sm, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={actionLinkDraft.required_for_closure}
                  onChange={(e) =>
                    setActionLinkDraft((prev) => ({ ...prev, required_for_closure: e.target.checked }))
                  }
                />
                Required for closure
              </label>
              <Button onClick={handleUpsertActionLink} disabled={loading} variant="secondary">
                Save Link
              </Button>
            </div>
          )}
        </div>

        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Escalation Rules</h4>
          <details>
            <summary style={{ cursor: "pointer", color: colors.textSecondary, fontSize: typography.size.sm }}>
              View escalation rules
            </summary>
            <div style={{ marginTop: spacing.md }}>
              {!rules && <div style={mutedTextStyle}>Rules unavailable.</div>}
              {rules && (
                <ul style={{ paddingLeft: spacing.md * 2 }}>
                  {Object.entries(rules).map(([severity, levels]) => (
                    <li key={severity}>
                      <strong>{severity}:</strong>{" "}
                      {Object.entries(levels && typeof levels === "object" ? levels : {})
                        .map(([lvl, target]) => `L${lvl} -> ${target}`)
                        .join(", ")}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </details>
        </div>
      </TabPanel>

      <TabPanel tabsId={tabsId} tabKey="related" activeTab={activeTab}>
        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Related Glyder Records</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: spacing.lg }}>
            <LinkedEntityMultiSelect
              label="Linked Risks"
              entityLabel="Risk"
              selectedIds={draft?.risk_ids || []}
              options={linkedRiskOptions}
              disabled={!canEdit}
              loading={linkedOptionsLoading.risks}
              onChange={(riskIds) => setDraft((prev) => ({ ...prev, risk_ids: riskIds }))}
            />
            <LinkedEntityMultiSelect
              label="Linked Controls"
              entityLabel="Control"
              selectedIds={draft?.control_ids || []}
              options={linkedControlOptions}
              disabled={!canEdit}
              loading={linkedOptionsLoading.controls}
              onChange={(controlIds) => setDraft((prev) => ({ ...prev, control_ids: controlIds }))}
            />
            <LinkedEntityMultiSelect
              label="Linked Actions"
              entityLabel="Action"
              selectedIds={draft?.action_ids || []}
              options={linkedActionOptions}
              disabled={!canEdit}
              loading={linkedOptionsLoading.actions}
              onChange={(actionIds) => setDraft((prev) => ({ ...prev, action_ids: actionIds }))}
            />
          </div>
        </div>

        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Related KPIs</h4>
          <RelatedKpisPanel entityType="incident" entityId={incident.id} />
        </div>
      </TabPanel>

      <TabPanel tabsId={tabsId} tabKey="evidence" activeTab={activeTab}>
        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Notes</h4>
          {notes.length === 0 && <div style={mutedTextStyle}>No notes yet.</div>}
          <ul style={{ paddingLeft: spacing.md * 2 }}>
            {notes.map((n) => (
              <li key={n.id} style={{ marginBottom: spacing.sm }}>
                <div>
                  <strong>{n.author || "user"}:</strong> {n.body}
                </div>
                <div style={metaTextStyle}>{formatDateTime(n.created_at)}</div>
              </li>
            ))}
          </ul>
          <div style={{ display: "flex", gap: spacing.md, marginBottom: spacing.md }}>
            <Input
              type="text"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add note"
              style={{ flex: 1, padding: spacing.sm }}
              disabled={!canEdit}
            />
            <Button onClick={handleAddNote} disabled={!canEdit || loading || !noteText.trim()} variant="secondary">
              Add
            </Button>
          </div>
          <div style={{ ...mutedTextStyle, marginBottom: spacing.md }}>
            Notes are stored with timestamps; editing not yet supported.
          </div>
        </div>

        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Evidence</h4>
          {attachments.length === 0 && <div style={mutedTextStyle}>No attachments yet.</div>}
          <ul style={{ paddingLeft: spacing.md * 2 }}>
            {attachments.map((a) => (
              <li key={a.id} style={{ marginBottom: spacing.md }}>
                <div>
                  <strong>{a.original_filename || a.safe_filename || a.filename || a.url || "Attachment"}</strong>{" "}
                  {a.size != null && <span>({(a.size / 1024).toFixed(1)} KB)</span>}{" "}
                  {a.storage_status === "missing" && <span style={{ color: colors.danger }}>missing</span>}
                </div>
                <div style={metaTextStyle}>Stored as: {a.safe_filename || a.filename || "-"}</div>
                <div style={metaTextStyle}>{formatDateTime(a.created_at)}</div>
                <div style={metaTextStyle}>Scan: {a.scan_status || "not_scanned"}</div>
                {a.scan_note && <div style={metaTextStyle}>Scan note: {a.scan_note}</div>}
                <div style={metaTextStyle}>Legal hold: {a.legal_hold ? "Yes" : "No"}</div>
                <div style={metaTextStyle}>Retention until: {formatDateTime(a.retention_until)}</div>
                <div style={metaTextStyle}>Checksum: {a.checksum ? `${a.checksum.slice(0, 12)}...` : "-"}</div>
                <div style={metaTextStyle}>Checksum verified: {formatDateTime(a.checksum_verified_at)}</div>
                <Button
                  style={{ marginTop: spacing.xs }}
                  onClick={async () => {
                    try {
                      const res = await downloadAttachment(a.id);
                      const blob = res?.data || res;
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = a.safe_filename || a.filename || a.original_filename || "attachment";
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      setError(extractApiError(err, "Download failed"));
                    }
                  }}
                  variant="secondary"
                >
                  Download
                </Button>
              </li>
            ))}
          </ul>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={async (e) => {
              e.preventDefault();
              if (!canEdit) return;
              const file = e.dataTransfer.files?.[0];
              if (!file) return;
              setUploading(true);
              setUploadError("");
              const formData = new FormData();
              formData.append("file", file);
              try {
                await addIncidentAttachment(incident.id, formData);
                await loadAttachments(incident.id);
                await loadHistory(incident.id);
              } catch (err) {
                setUploadError(extractApiError(err, "Upload failed"));
              } finally {
                setUploading(false);
              }
            }}
            style={{
              border: `1px dashed ${colors.borderStrong}`,
              padding: spacing.xl,
              borderRadius: radius.sm,
              marginTop: spacing.md,
              background: uploading ? colors.accentSubtle : colors.surfaceSubtle,
              color: colors.textMuted,
            }}
          >
            Drag & drop to upload (or click)
            <Input
              type="file"
              style={{ display: "block", marginTop: spacing.sm }}
              disabled={!canEdit || uploading}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploading(true);
                setUploadError("");
                const formData = new FormData();
                formData.append("file", file);
                try {
                  await addIncidentAttachment(incident.id, formData);
                  await loadAttachments(incident.id);
                  await loadHistory(incident.id);
                } catch (err) {
                  setUploadError(extractApiError(err, "Upload failed"));
                } finally {
                  setUploading(false);
                  e.target.value = "";
                }
              }}
            />
            {uploadError && <div style={{ color: colors.danger }}>{uploadError}</div>}
            {uploading && <div style={mutedTextStyle}>Uploading...</div>}
          </div>
        </div>
      </TabPanel>

      <TabPanel tabsId={tabsId} tabKey="history" activeTab={activeTab}>
        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>History</h4>
          {history.length === 0 && <div style={mutedTextStyle}>No history yet.</div>}
          <ul style={{ paddingLeft: spacing.md * 2 }}>
            {history.map((h) => (
              <li key={h.id} style={{ marginBottom: spacing.sm }}>
                [{formatDateTime(h.created_at)}] {h.field}: {h.old_value || "-"} -> {h.new_value || "-"}{" "}
                {h.actor ? `by ${h.actor}` : ""}
              </li>
            ))}
          </ul>
        </div>

        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Timeline</h4>
          {!timeline && <div style={mutedTextStyle}>Timeline unavailable.</div>}
          {timeline && (
            <ul style={{ paddingLeft: spacing.md * 2 }}>
              <li>Reported: {formatDateTime(timeline.reported_at)}</li>
              <li>SLA Due: {formatDateTime(timeline.sla_due_at)}</li>
              <li>Breach: {formatDateTime(timeline.breach_at)}</li>
              <li>Escalated: {formatDateTime(timeline.escalated_at)}</li>
              {timeline.escalations?.length > 0 && (
                <li>
                  Escalations:
                  <ul>
                    {timeline.escalations.map((e, idx) => (
                      <li key={idx}>
                        {formatDateTime(e.at)}: {e.value}
                      </li>
                    ))}
                  </ul>
                </li>
              )}
            </ul>
          )}
        </div>
      </TabPanel>
      <DestructiveConfirm
        actionId="INCIDENT_STATUS_UPDATE"
        open={confirmClose}
        resourceDisplayName={incident?.title}
        context={{ scopeLabel: "Close incident" }}
        onCancel={() => setConfirmClose(false)}
        onConfirm={() => {
          setConfirmClose(false);
          handleCloseIncident();
        }}
      />
      <DestructiveConfirm
        actionId="INCIDENT_ESCALATE"
        open={confirmEscalate}
        resourceDisplayName={incident?.title}
        context={{ scopeLabel: "Escalate incident" }}
        onCancel={() => setConfirmEscalate(false)}
        onConfirm={() => {
          setConfirmEscalate(false);
          handleEscalate();
        }}
      />
    </div>
  );
}
